import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";
import { Box, Grid, IconButton, Stack, Switch } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VSNImage from "../../shared/VSNImage";
import { ReactComponent as Round } from "../../assets/admin/svg/org-image.svg";
import VSNTypography from "../../shared/VSNTypography";
import { upperCase } from "../../utils/javascript";
import VSNButton from "../../shared/VSNButton";
import VSNLabel from "../../shared/VSNLabel";
import { toggleModelShow } from "../../shared/CustomFunc";
import shape1 from "../../assets/admin/png/model-shape-1.png";
import shape2 from "../../assets/admin/png/model-shape-2.png";
import { ApiContainer } from "../../utils/api";
import VSNLoader from "../../shared/VSNLoader";

const formPath = { parent: "eventScheduleInfo" };
const useStyles = makeStyles((theme) => ({
  heading: {
    position: "sticky",
    top: 0,
    borderBottom: "1px solid var(--primary)",
    padding: "12px 25px",
    margin: "0 -25px",
    backgroundColor: "var(--white)",
    zIndex: 2,
    [theme.breakpoints.down("sm")]: {
      padding: "12px 15px",
      margin: "0 -15px",
    },
    "&:after": {
      position: "absolute",
      content: "''",
      left: 0,
      right: 0,
      height: 25,
      width: "100%",
      bottom: "100%",
      background: "var(--white)",
      zIndex: 8,
    },
    "& svg": {
      borderRadius: "50%",
      [theme.breakpoints.down("md")]: {
        width: 40,
        height: 40,
      },
      [theme.breakpoints.down("sm")]: {
        width: 30,
        height: 30,
      },
    },
    "& h5": {
      fontSize: 24,
      fontWeight: 700,
      lineHeight: "31px",
      marginLeft: 16,
      [theme.breakpoints.down("md")]: {
        fontSize: 18,
        lineHeight: "26px",
        maxWidth: "70%",
      },
    },
  },
  detailHeading: {
    "&.MuiTypography-root": {
      color: "rgba(134, 101, 10, 1)",
      fontSize: 14,
      fontWeight: 700,
      marginBottom: 24,
      [theme.breakpoints.down("md")]: {
        marginBottom: 20,
      },
    },
  },
  cancelButton: {
    "&.MuiButtonBase-root": {
      padding: [[12, 62]],
      [theme.breakpoints.down("md")]: {
        padding: [[8, 15]],
      },
    },
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "#F6F6FD",
    padding: "20px 25px",
    gap: 10,
    marginLeft: "-25px",
    marginRight: "-25px",
    position: "sticky",
    bottom: 0,
    zIndex: 99,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      padding: "15px",
      gap: 10,
      marginLeft: "-15px",
      marginRight: "-15px",
    },
    "& .MuiButtonBase-root": {
      margin: 0,
      padding: "9.5px 25px",
      lineHeight: "21px",
      width: "auto",
      minWidth: "170px",
      borderRadius: 10,
      "& span": {
        fontSize: 16,
        fontWeight: 700,
      },
      [theme.breakpoints.down("sm")]: {
        width: "100%",
      },
    },
  },
  shape: {
    position: "absolute",
    zIndex: -2,
  },
  shape1: {
    top: "20%",
    right: "10%",
  },
  shape2: {
    bottom: "5%",
    left: "0%",
  },
  formGrid: {
    "& .react-tel-input": {
      "& .form-control": {
        fontSize: 18,
        fontWeight: 700,
        color: "#202024",
      },
    },
    "& .MuiFormControl-root.MuiTextField-root": {
      width: "100%",
    },
  },
  downloadEventRosterBtn: {
    "&.MuiButtonBase-root": {
      borderRadius: "8px",
      border: "1px solid var(--primary)",
      width: "100%",
      "&.MuiIconButton-root": {
        fontSize: "14px",
        gap: "10px",
        color: "var(--primary)",
        fontWeight: 700,
      },
    },
  },
}));

const ViewEventSchedule = () => {
  const classes = useStyles();
  const { api } = ApiContainer();
  const dispatch = useDispatch();
  const eventScheduleInfo = useSelector(
    (state) => state.form.formData?.[formPath.parent]
  );
  const loader = useSelector((state) => state.api?.loader?.viewEventSchedule);

  const { organization_original_id, location_id, department_id } = useSelector(
    (state) => state?.app?.userData?.user_profile
  );

  const handledownloadCSVFile = async () => {
    const data = await api({
      method: "GET",
      endPoint: `/organization/${organization_original_id}/locations/${location_id}/departments/${department_id}/event-schedule/${eventScheduleInfo?.id}/event-roster/download`,
      showToastMessage: false,
      urlencoded: false,
      needLoader: true,
      parent: "downloadCSVFile",
      needFullResponse: false,
    });

    if (data?.status && data?.data) {
      const csvContent = data?.data;
      const blob = new Blob([csvContent], {
        type: "text/csv;charset=utf-8;",
      });

      const fileName = `Event_Schedule_${eventScheduleInfo?.event_name}.csv`;
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const createEventScheduleAttribute = [
    {
      label: "Event Summary",
      subTitle: "Enter, review and confirm key event information",
      md: 12,
      value: [
        {
          control: "input",
          label: "Event Name",
          name: "event_name",
          value: "",
          type: "text",
          fullWidth: true,
          md: 4,
        },
        {
          control: "date",
          label: "Event Start Date",
          name: "event_timing_start_date",
          value: null,
          validateAs: "date",
          fullWidth: true,
          md: 4,
        },
        {
          control: "date",
          label: "Event End Date",
          name: "event_timing_end_date",
          value: "",
          validateAs: "date",
          fullWidth: true,
          md: 4,
        },
        {
          control: "button",
          label: "Download CSV File",
          name: "id",
        },
      ],
    },
    {
      label: "Lab Draws",
      subTitle: "Assign the lab draw type and select draw dates.",
      md: 12,
      value: [
        {
          control: "select",
          label: "Lab Draw Type",
          name: "lab_draw_type",
          value: 0,
          isRequired: true,
          fullWidth: true,
          md: 4,
        },
        {
          control: "time",
          label: "Start Time",
          name: "lab_draws_start_time",
          value: null,
          isRequired: true,
          validateAs: "time",
          fullWidth: true,
          md: 4,
        },
        {
          control: "time",
          label: "End Time",
          name: "lab_draws_end_time",
          value: null,
          isRequired: true,
          fullWidth: true,
          md: 4,
        },
        {
          control: "date",
          label: "Start Date",
          name: "lab_draws_start_date",
          value: null,
          isRequired: true,
          validateAs: "date",
          fullWidth: true,
          md: 6,
        },
        {
          control: "date",
          label: "End Date",
          name: "lab_draws_end_date",
          value: "",
          isRequired: true,
          fullWidth: true,
          md: 6,
        },
        ...(eventScheduleInfo?.lab_draw_type === "on_site"
          ? [
              {
                control: "input",
                label: "Address Line 1",
                type: "text",
                name: "lab_draw_address_line_1",
                value: "",
                fullWidth: true,
                md: 6,
              },
              {
                control: "input",
                label: "Address Line 2",
                type: "text",
                name: "lab_draw_address_line_2",
                value: "",
                fullWidth: true,
                md: 6,
              },
              {
                control: "input",
                label: "City",
                type: "text",
                name: "lab_draw_city",
                value: "",
                fullWidth: true,
                md: 6,
              },
              {
                control: "input",
                label: "Zip Code",
                type: "number",
                name: "lab_draw_zip_code",
                value: "",
                fullWidth: true,
                md: 6,
                sx: {
                  '& input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button':
                    {
                      WebkitAppearance: "none",
                    },
                },
              },
            ]
          : []),
      ],
    },
    {
      label: "ANNUAL PHYSICALS",
      subTitle:
        "Schedule the annual physical assessments like X-rays and CPETs",
      md: 12,
      value: [
        {
          control: "date",
          label: "Start Date",
          name: "diagnostic_testing_start_date",
          value: null,
          fullWidth: true,
          md: 6,
        },
        {
          control: "date",
          label: "End Date",
          name: "diagnostic_testing_end_date",
          value: "",
          fullWidth: true,
          md: 6,
        },
        {
          control: "input",
          label: "Address Line 1",
          type: "text",
          name: "diagnostic_testing_address_line_1",
          value: "",
          fullWidth: true,
          md: 6,
        },
        {
          control: "input",
          label: "Address Line 2",
          type: "text",
          name: "diagnostic_testing_address_line_2",
          value: "",
          fullWidth: true,
          md: 6,
        },
        {
          control: "input",
          label: "City",
          type: "text",
          name: "diagnostic_testing_city",
          value: "",
          fullWidth: true,
          md: 6,
        },
        {
          control: "input",
          label: "Zip Code",
          type: "number",
          name: "diagnostic_testing_zip_code",
          value: "",
          fullWidth: true,
          md: 6,
        },
      ],
    },
    {
      label: "Provider Exam",
      subTitle: "Set up provider exam dates and slots.",
      md: 12,
      value: [
        {
          control: "date",
          label: "Start Date",
          name: "provider_exam_start_date",
          value: null,
          fullWidth: true,
          md: 6,
        },
        {
          control: "date",
          label: "End Date",
          name: "provider_exam_end_date",
          value: "",
          fullWidth: true,
          md: 6,
        },
      ],
    },
    {
      label: "EMAIL FLOW",
      subTitle:
        "Automatically send scheduled emails to participants during the event lifecycle.",
      md: 12,
      value: [
        {
          control: "switch",
          label: "Would you like to enable sending event schedule emails?",
          name: "send_emails",
          md: 12,
        },
        {
          control: "input",
          label: "Additional Note",
          name: "additional_note",
          value: "",
          fullWidth: true,
          md: 12,
        },
      ],
    },
  ];

  if (loader) {
    return <VSNLoader variant="fullPage" />;
  }
  return (
    <Box>
      <>
        <VSNImage
          src={shape1}
          alt=""
          className={`${classes.shape} ${classes.shape1}`}
        />
        <VSNImage
          src={shape2}
          alt=""
          className={`${classes.shape} ${classes.shape2}`}
        />
        <Stack direction="row" alignItems="center" className={classes.heading}>
          <Stack>
            <Round />
          </Stack>
          <VSNTypography variant="h5" component="h5">
            {eventScheduleInfo?.event_name ?? "Event Details"}
          </VSNTypography>
        </Stack>
      </>

      <Grid container spacing={3} py={3}>
        {createEventScheduleAttribute?.map(
          ({ label, value, md, subTitle }, sectionIndex) => (
            <Grid item xs={12} md={md}>
              <Stack
                sx={{
                  padding: { sm: 3, xs: 2 },
                  background: "#ffffff",
                  borderRadius: 2,
                  boxShadow: "0px 0px 14px 0px #0000001A",
                  height: "100%",
                }}
              >
                <VSNTypography
                  sx={{ color: "#86650A", fontWeight: 700, fontSize: 14 }}
                >
                  {upperCase(label)}
                </VSNTypography>
                <VSNTypography
                  sx={{ color: "#3A3A40", fontWeight: 400, fontSize: 16 }}
                >
                  {subTitle}
                </VSNTypography>
                <Grid
                  container
                  rowSpacing={{ xs: 0, md: 2 }}
                  columnSpacing={4}
                  className={classes.formGrid}
                  pt={3}
                >
                  {value?.map((props, index) => {
                    const {
                      isRequired,
                      label,
                      name,
                      value,
                      md,
                      doNotShow,
                      control,
                      readOnly,
                      ...attribute
                    } = props;

                    return (
                      !doNotShow &&
                      eventScheduleInfo?.[name] && (
                        <Grid
                          item
                          xs={12}
                          xsm={6}
                          md={md}
                          key={index}
                          mb={{ xs: 2, md: 0 }}
                        >
                          <VSNLabel required={isRequired} label={label} />
                          <Box fontWeight={700} fontSize={18}>
                            {label === "Download CSV File"
                              ? " "
                              : eventScheduleInfo?.[name]}
                          </Box>
                          {control === "switch" && (
                            <Switch
                              checked={eventScheduleInfo?.send_emails || false}
                            />
                          )}

                          {control === "button" && (
                            <IconButton
                              onClick={() => handledownloadCSVFile()}
                              className={classes.downloadEventRosterBtn}
                            >
                              <FileDownloadIcon
                                style={{ color: "var(--primary)" }}
                              />
                              Download Event Roster CSV
                            </IconButton>
                          )}
                        </Grid>
                      )
                    );
                  })}
                </Grid>
              </Stack>
            </Grid>
          )
        )}
      </Grid>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={2}
        className={classes.buttons}
      >
        <VSNButton
          handleClick={() =>
            dispatch(toggleModelShow("viewEventSchedule", true))
          }
          variant="outlined"
          className={classes.cancelButton}
        >
          Cancel
        </VSNButton>
      </Stack>
    </Box>
  );
};

export default ViewEventSchedule;
